import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { canAccessAdmin } from '../../utils/roleUtils';
import { 
  BarChart3, PieChart, TrendingUp, Download, Calendar, 
  Filter, Search, FileText, AlertCircle, CheckCircle
} from 'lucide-react';
import '../../styles/modern-design-system.css';
import '../../styles/agency-admin.css';

const Reports = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState('last_30_days');
  const [searchQuery, setSearchQuery] = useState('');

  // Check if user has agency admin access
  const hasAgencyAdminAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  useEffect(() => {
    if (!hasAgencyAdminAccess()) {
      navigate('/agent/dashboard');
      return;
    }
    fetchReports();
  }, [user, navigate]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // Mock data for now
      setReports([
        {
          id: 1,
          name: 'Revenue Report',
          description: 'Comprehensive revenue analysis and trends',
          type: 'financial',
          icon: TrendingUp,
          color: 'green',
          lastGenerated: '2024-01-15',
          frequency: 'monthly',
          status: 'active'
        },
        {
          id: 2,
          name: 'Agent Performance',
          description: 'Individual agent performance metrics',
          type: 'performance',
          icon: BarChart3,
          color: 'blue',
          lastGenerated: '2024-01-14',
          frequency: 'weekly',
          status: 'active'
        },
        {
          id: 3,
          name: 'Client Analytics',
          description: 'Client acquisition and retention analysis',
          type: 'analytics',
          icon: PieChart,
          color: 'purple',
          lastGenerated: '2024-01-13',
          frequency: 'monthly',
          status: 'active'
        },
        {
          id: 4,
          name: 'Commission Report',
          description: 'Commission tracking and payout analysis',
          type: 'financial',
          icon: FileText,
          color: 'orange',
          lastGenerated: '2024-01-12',
          frequency: 'weekly',
          status: 'active'
        },
        {
          id: 5,
          name: 'Trip Analytics',
          description: 'Trip booking patterns and destination analysis',
          type: 'analytics',
          icon: BarChart3,
          color: 'indigo',
          lastGenerated: '2024-01-11',
          frequency: 'monthly',
          status: 'active'
        },
        {
          id: 6,
          name: 'Financial Summary',
          description: 'Complete financial overview and P&L',
          type: 'financial',
          icon: TrendingUp,
          color: 'green',
          lastGenerated: '2024-01-10',
          frequency: 'monthly',
          status: 'active'
        }
      ]);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (reportId) => {
    try {
      // In real implementation, generate report via API
      console.log('Generating report:', reportId);
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const handleExportReport = async (reportId, format) => {
    try {
      // In real implementation, export report via API
      console.log('Exporting report:', reportId, 'as', format);
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         report.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (!hasAgencyAdminAccess()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Reports & Analytics.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="flex-1 p-6">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
                <p className="text-gray-600 mt-2">Generate and export comprehensive business reports</p>
              </div>
              <div className="flex gap-2">
                <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Calendar className="w-4 h-4 mr-2" />
                  Date Range
                </button>
                <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <FileText className="w-4 h-4 mr-2" />
                  Create Report
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_90_days">Last 90 Days</option>
                <option value="this_year">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </button>
            </div>
          </div>

          {/* Reports Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredReports.map((report) => (
              <div key={report.id} className="report-card">
                <div className="flex items-start justify-between mb-4">
                  <div className={`report-icon bg-${report.color}-100`}>
                    <report.icon className={`w-6 h-6 text-${report.color}-600`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`status-indicator status-${report.status}`}>
                      {report.status}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <h3 className="report-title">{report.name}</h3>
                  <p className="report-description">{report.description}</p>
                </div>

                <div className="mb-4 text-sm text-gray-500">
                  <div className="flex items-center justify-between">
                    <span>Last generated:</span>
                    <span>{new Date(report.lastGenerated).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Frequency:</span>
                    <span className="capitalize">{report.frequency}</span>
                  </div>
                </div>

                <div className="report-actions">
                  <button 
                    onClick={() => handleGenerateReport(report.id)}
                    className="report-btn report-btn-primary"
                  >
                    Generate
                  </button>
                  <button 
                    onClick={() => handleExportReport(report.id, 'pdf')}
                    className="report-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </button>
                  <button 
                    onClick={() => handleExportReport(report.id, 'excel')}
                    className="report-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Excel
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredReports.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your search criteria.</p>
            </div>
          )}

          {/* Quick Stats */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Reports</p>
                  <p className="text-2xl font-bold text-gray-900">{reports.length}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Generated Today</p>
                  <p className="text-2xl font-bold text-gray-900">3</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">1</p>
                </div>
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Exported This Month</p>
                  <p className="text-2xl font-bold text-gray-900">24</p>
                </div>
                <Download className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
      </main>
    </div>
  );
};

export default Reports;
